import React, { useState, useEffect } from 'react';
import { Card, List, Button, Typography, Space, Tag, Statistic, Empty } from 'antd';
import { DownloadOutlined, LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { videoApi } from '../services/api';
import { normalizeExportJobsResponse } from '../utils/exportJobs';

const { Text } = Typography;

/**
 * Recent Exports Panel Component
 * Displays list of recent export jobs with status and download links
 */
const RecentExportsPanel = ({ onRefresh }) => {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0 });
  
  useEffect(() => {
    fetchExports();
  }, [pagination.offset]);
  
  const fetchExports = async () => {
    try {
      setLoading(true);
      const result = await videoApi.listExportJobs({ 
        limit: pagination.limit, 
        offset: pagination.offset 
      });
      const normalizedResult = normalizeExportJobsResponse(result);
      setExports(normalizedResult.jobs);
      setPagination(prev => ({ ...prev, total: normalizedResult.total }));
    } catch (error) {
      console.error('Error fetching export jobs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = () => {
    fetchExports();
    onRefresh?.();
  };

  const handleDownload = async (exportJobId) => {
    try {
      setDownloadingId(exportJobId);
      const exportJob = await videoApi.getExportJobStatus(exportJobId);
      if (!exportJob.download_url) {
        throw new Error('Export download link is not available');
      }

      const link = document.createElement('a');
      link.href = exportJob.download_url;
      link.download = exportJob.download_filename || 'video-analysis-export.zip';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading export job:', error);
    } finally {
      setDownloadingId(null);
    }
  };
  
  const statusConfig = {
    pending: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Pending' },
    processing: { color: 'orange', icon: <LoadingOutlined spin />, text: 'Processing' },
    ready: { color: 'green', icon: <CheckCircleOutlined />, text: 'Ready' },
    failed: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Failed' },
    expired: { color: 'default', icon: <ExclamationCircleOutlined />, text: 'Expired' }
  };
  
  const renderExportItem = (item) => {
    const status = statusConfig[item.status] || statusConfig.pending;
    const progress = item.progress || {
      processed: 0,
      failed: 0,
      total: 0,
      completed: 0,
      percentage: 0
    };
    const fileSizeMB = item.output_file_size 
      ? (item.output_file_size / 1024 / 1024).toFixed(2) 
      : null;
    const createdAt = new Date(item.created_at).toLocaleString();
    const completedAt = item.completed_at ? new Date(item.completed_at).toLocaleString() : null;
    const expiresAt = item.download_expires_at 
      ? new Date(item.download_expires_at).toLocaleString()
      : null;
    
    const progressPercentage = progress.percentage || 0;
    
    return (
      <List.Item
        key={item.export_job_id}
        actions={[
          item.status === 'ready' && (
            <Button 
              type="primary" 
              size="small"
              icon={<DownloadOutlined />}
              loading={downloadingId === item.export_job_id}
              onClick={() => handleDownload(item.export_job_id)}
            >
              Download
            </Button>
          )
        ].filter(Boolean)}
      >
        <List.Item.Meta
          title={
            <Space>
              <Tag icon={status.icon} color={status.color}>{status.text}</Tag>
              <Text strong>{progress.total} videos</Text>
            </Space>
          }
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space split="|">
                <Text type="secondary">Created: {createdAt}</Text>
                {completedAt && <Text type="secondary">Completed: {completedAt}</Text>}
              </Space>
              
              {item.status === 'processing' && (
                <Space>
                  <Text>{progressPercentage}% complete</Text>
                  <Text type="secondary">
                    ({progress.completed}/{progress.total} processed)
                  </Text>
                </Space>
              )}
              
              {item.status === 'ready' && (
                <Space split="|">
                  {fileSizeMB && <Text>Size: {fileSizeMB} MB</Text>}
                  {progress.failed > 0 && (
                    <Text type="warning">{progress.failed} failed</Text>
                  )}
                  {expiresAt && <Text type="secondary">Expires: {expiresAt}</Text>}
                </Space>
              )}
              
              {item.status === 'failed' && item.error_message && (
                <Text type="danger">{item.error_message}</Text>
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };
  
  return (
    <Card 
      title="Recent Exports"
      extra={
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      }
      style={{ marginTop: '2rem' }}
    >
      <List
        loading={loading}
        dataSource={exports}
        renderItem={renderExportItem}
        locale={{
          emptyText: (
            <Empty 
              description="No export jobs yet" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
        pagination={
          pagination.total > pagination.limit
            ? {
                current: Math.floor(pagination.offset / pagination.limit) + 1,
                pageSize: pagination.limit,
                total: pagination.total,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} exports`,
                onChange: (page) => {
                  setPagination(prev => ({
                    ...prev,
                    offset: (page - 1) * prev.limit
                  }));
                }
              }
            : false
        }
      />
    </Card>
  );
};

export default RecentExportsPanel;

